import { Controller } from '@hotwired/stimulus'

// @-mention autocomplete for the comment composer. Detects an "@query" at the
// caret, fetches matching users from /mentions/search, and on pick inserts
// "@handle " into the textarea plus a hidden comment[mention_user_ids][] input so
// the server can tokenize the body. A picked id whose @handle is later deleted is
// simply ignored server-side, so no cleanup is needed. Auto-registered by the glob.
export default class extends Controller {
  static targets = ['field', 'menu', 'mentions']
  static values = { url: String }

  // matches an "@query" that ends at the caret (start of line or after whitespace)
  static QUERY = /(?:^|\s)@([A-Za-z0-9_-]*)$/

  connect() {
    this.close()
    this._onDocClick = (e) => { if (!this.element.contains(e.target)) this.close() }
    document.addEventListener('click', this._onDocClick)
  }

  disconnect() {
    document.removeEventListener('click', this._onDocClick)
    clearTimeout(this._debounce)
  }

  // input -> mention-autocomplete#search (debounced)
  search() {
    clearTimeout(this._debounce)
    const query = this.activeQuery()
    if (query === null) return this.close()
    this._debounce = setTimeout(() => this.fetchUsers(query), 150)
  }

  async fetchUsers(query) {
    let users
    try {
      const res = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`,
                              { headers: { Accept: 'application/json' } })
      if (!res.ok) return this.close()
      users = await res.json()
    } catch {
      return this.close()
    }
    this.renderMenu(users)
  }

  // keydown -> mention-autocomplete#keydown: Esc closes an open menu (and stops
  // the keypress from also closing a surrounding dialog/reply form).
  keydown(event) {
    if (event.key === 'Escape' && this.hasMenuTarget && !this.menuTarget.hidden) {
      event.stopPropagation()
      this.close()
    }
  }

  activeQuery() {
    const el = this.fieldTarget
    const m = el.value.slice(0, el.selectionStart).match(this.constructor.QUERY)
    return m ? m[1] : null
  }

  renderMenu(users) {
    if (!users.length) return this.close()
    this.menuTarget.replaceChildren()
    users.forEach((user) => this.menuTarget.appendChild(this.menuItem(user)))
    this.menuTarget.hidden = false
  }

  // Built with textContent (never innerHTML) so a user's name/handle can't inject HTML.
  menuItem(user) {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'flex w-full items-baseline gap-2 px-3 py-2 text-left text-sm hover:bg-surface-2'
    const handle = document.createElement('span')
    handle.className = 'font-medium text-heading'
    handle.textContent = `@${user.handle}`
    btn.appendChild(handle)
    if (user.name) {
      const name = document.createElement('span')
      name.className = 'truncate text-muted'
      name.textContent = user.name
      btn.appendChild(name)
    }
    // mousedown (not click) so it fires before the textarea blur closes the menu.
    btn.addEventListener('mousedown', (e) => { e.preventDefault(); this.pick(user) })
    return btn
  }

  pick(user) {
    const el = this.fieldTarget
    const caret = el.selectionStart
    const m = el.value.slice(0, caret).match(this.constructor.QUERY)
    if (!m) return this.close()

    const at = caret - m[1].length - 1 // index of the '@'
    const before = el.value.slice(0, at)
    const insert = `@${user.handle} `
    el.value = before + insert + el.value.slice(caret)
    const pos = before.length + insert.length
    el.setSelectionRange(pos, pos)
    el.focus()
    el.dispatchEvent(new Event('input', { bubbles: true })) // keep char-counter in sync

    this.addMention(user.id)
    this.close()
  }

  addMention(id) {
    if (this.mentionsTarget.querySelector(`input[value="${CSS.escape(id)}"]`)) return
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'comment[mention_user_ids][]'
    input.value = id
    this.mentionsTarget.appendChild(input)
  }

  close() {
    if (!this.hasMenuTarget) return
    this.menuTarget.hidden = true
    this.menuTarget.replaceChildren()
  }
}
